import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useAcceptDtTerms, useOrg, useOrgs } from 'src/hooks/query/orgHooks';
import { useUser } from 'src/hooks/query/userHooks';
import NavOrgSwitcher from 'src/layouts/app/nav/NavOrgSwitcher';
import { toast } from 'sonner';
import Markdown from 'src/components/markdown';
import userAgreementMarkdown from './datatrust-user-agreement.md?raw';
import { useAuth0 } from '@auth0/auth0-react';

export default function TermsAcceptanceModal() {
  const { data: currentUser } = useUser();
  const [signatureName, setSignatureName] = useState('');
  const acceptTermsMutation = useAcceptDtTerms();
  const { logout } = useAuth0();

  const { data: currentOrg } = useOrg();
  const { data: orgs = [], isPending: orgsLoading } = useOrgs();

  const expectedSignatureName =
    `${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim();

  const canAccept = !!signatureName.trim() && signatureName.trim() === expectedSignatureName;

  const handleAccept = async () => {
    if (!currentOrg) return;

    try {
      await acceptTermsMutation.mutateAsync({ data: null });
      toast.success('Terms accepted. Check your email for a copy of your signed terms.');
    } catch {
      toast.error('Failed to accept terms');
    }
  };

  return (
    <Modal open={true} disableEscapeKeyDown>
      <Card
        sx={{
          position: 'absolute',
          maxWidth: 860,
          width: { xs: '90vw', sm: 720, md: 780 },
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          outline: 'none',
          borderRadius: 3,
          boxShadow: (theme) => theme.shadows[24],
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Stack spacing={2.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Review & sign
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  Please review The Data Trust end user license agreement before continuing.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexShrink: 0,
                  alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                }}
              >
                {!orgsLoading && orgs.length > 1 && (
                  <NavOrgSwitcher triggerComponent={TermsModalOrgSwitcherButton} />
                )}
                <Typography variant="body2" sx={{ color: 'divider' }}>
                  |
                </Typography>
                <Button
                  variant="text"
                  color="inherit"
                  size="small"
                  onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                  sx={{ minWidth: 'auto', px: 0, color: 'text.secondary' }}
                >
                  Sign out
                </Button>
              </Box>
            </Box>

            <Card
              sx={{
                width: '100%',
                minHeight: 320,
                maxHeight: { xs: '36vh', sm: '40vh', md: 380 },
                position: 'relative',
                borderRadius: 2,
                border: '1px solid rgba(145, 158, 171, 0.24)',
                overflow: 'hidden',
                display: 'flex',
                boxShadow: 'none',
              }}
            >
              <CardContent
                sx={{
                  flex: 1,
                  p: 0,
                  overflow: 'auto',
                }}
              >
                <Markdown
                  sx={{
                    fontSize: 15,
                    lineHeight: 1.7,
                    color: 'text.primary',
                    '& > *:first-of-type': { mt: 0 },
                    '& p': { mt: 0, mb: 2, fontSize: 15, lineHeight: 1.7 },
                    '& p:last-child': { mb: 0 },
                    '& li + li': { mt: 1.5 },
                    '& br': { display: 'block', content: '""', mb: 1 },
                    '& h2': { my: 2, fontSize: 15, fontWeight: 700, lineHeight: 1.7 },
                  }}
                >
                  {userAgreementMarkdown}
                </Markdown>
              </CardContent>
            </Card>

            <Box
              sx={{
                px: 0.5,
              }}
            >
              <TextField
                variant="standard"
                placeholder="Type your full name"
                value={signatureName}
                onChange={(event) => setSignatureName(event.target.value)}
                helperText={expectedSignatureName || currentUser?.email || ''}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontFamily: '"Snell Roundhand", "Brush Script MT", "Segoe Script", cursive',
                    fontSize: { xs: 26, sm: 30 },
                    lineHeight: 1.1,
                    px: 0,
                    py: 0.25,
                  },
                }}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    alignItems: 'flex-end',
                  },
                }}
              />
            </Box>

            <Divider />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                pt: 0.5,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
                By typing your full legal name and clicking &quot;Sign & Continue,&quot; you agree
                that your typed name constitutes your electronic signature and that you agree to the
                User Agreement.
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'stretch', sm: 'flex-end' },
                }}
              >
                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleAccept}
                  disabled={!canAccept}
                  loading={acceptTermsMutation.isPending}
                  sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
                >
                  Sign & Continue
                </Button>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Modal>
  );
}

const TermsModalOrgSwitcherButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button
    onClick={onClick}
    variant="text"
    size="small"
    color="primary"
    sx={{
      minWidth: 'auto',
      px: 0,
      flexShrink: 0,
    }}
  >
    Switch organization
  </Button>
);
